// Settings — the overview (/pam/settings) and the MFA Provider page
// (/pam/settings/mfaProvider), both rendered inside SettingsShell and built from
// the shared settings primitives in ./settingsUi.
// The MFA Provider page is fully wired to the live GetMFAProvider/SetMFAProvider API;
// the overview rows show live status and open the other settings pages.
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, message, Modal } from "antd";
import axios from "axios";
import { ORG_ID, DOMAIN_ID, AUTH_TOKEN, REACT_APP_API_TENANTS_URL } from "../../constants";
import { getRecordingStorage } from "../../service/recordingStorageService";
import { listSsoProviders } from "../../service/ssoService";
import { isAdminUser } from "../../utils/helpers";
import { dispatchGetMFAProvider, dispatchSetMFAProvider, dispatchDeleteMFAProvider } from "../../utils/apiservice";
import { logoutHandler } from "../../utils/navigateHandler";
import { AN_COLORS, AN_FONT, AN_MONO, anAlpha } from "../../design-system/tokens";
import AuthenticatorSettings, { PushHistorySection } from "./AuthenticatorSettings";
import {
  SETTINGS_TOKENS,
  SettingsCard,
  SettingsField,
  SettingsPage,
  SettingsRow,
  SettingsSection,
  SettingsStatus,
} from "./settingsUi";

// Palette: the shared design tokens.
const C = AN_COLORS;

// ── Icon (ported subset of the design's stroked icon set) ──────────────────
const ICON_PATHS = {
  shield: <path d="M12 3l8 3v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-3z" />,
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33h0a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51h0a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v0a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </>
  ),
  logs: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </>
  ),
  doc: (
    <>
      <path d="M6 3h9l4 4v14a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z" />
      <path d="M14 3v5h5M8 13h8M8 17h5" />
    </>
  ),
  lock: (
    <>
      <rect x="4" y="11" width="16" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 018 0v3" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20v-1a5 5 0 015-5h2a5 5 0 015 5v1" />
      <path d="M16.5 5.6a3.2 3.2 0 010 5.3M18 14.2a4.4 4.4 0 013 4.2V20" />
    </>
  ),
  storage: (
    <>
      <ellipse cx="12" cy="5.5" rx="7.5" ry="2.8" />
      <path d="M4.5 5.5v6.5c0 1.5 3.4 2.8 7.5 2.8s7.5-1.3 7.5-2.8V5.5" />
      <path d="M4.5 12v6.5c0 1.5 3.4 2.8 7.5 2.8s7.5-1.3 7.5-2.8V12" />
    </>
  ),
  key: (
    <>
      <circle cx="7.5" cy="15.5" r="4" />
      <path d="M10.4 12.6L20 3M16 7l3 3M14 9l2 2" />
    </>
  ),
  check: <path d="M4 12l5 5 11-11" />,
  chevron: <path d="M9 6l6 6-6 6" />,
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
  arrowRt: <path d="M5 12h14M13 5l7 7-7 7" />,
  arrowLt: <path d="M19 12H5M11 5l-7 7 7 7" />,
};
function Icon({ name, size = 18, stroke = 1.6 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
      {ICON_PATHS[name]}
    </svg>
  );
}

// ── Data ───────────────────────────────────────────────────────────────────
// Settings sections shown as cards. `route` opens an existing page; the MFA card
// (no route) opens the in-page MFA panel.
const SECTION_META = [
  { id: "users", icon: "users", title: "Users & Access",
    sub: "Accounts on this tenant — roles, enrolled MFA factors, and account status.",
    meta: "Tenant accounts", action: "View",
    route: "/pam/settings/users" },
  { id: "mfa", icon: "shield", title: "MFA Provider",
    sub: "Choose how push MFA is delivered when users sign in with their directory account.",
    route: "/pam/settings/mfaProvider" },
  // The signed-in user's own second factors used to be a card of their own here.
  // They now live in the first panel of Tenant Configuration, next to the
  // tenant-wide MFA settings they interact with — hence the sub below leading
  // with them, so the function stays discoverable from this grid.
  { id: "tenant", icon: "gear", title: "Tenant Configuration",
    sub: "Your own MFA factors, plus MFA enforcement, policy controls and security defaults for your tenant.",
    route: "/pam/settings/tenantConfig" },
  { id: "logs", icon: "logs", title: "Logs Configuration",
    sub: "Forward authentication and policy events to your logging stack.",
    route: "/pam/settings/logsConfig" },
  { id: "recordings", icon: "storage", title: "Recording Storage",
    sub: "Keep SSH session recordings on the control plane, or in your own S3 or Google Cloud Storage bucket.",
    route: "/pam/settings/recordingStorage" },
  { id: "sso", icon: "key", title: "Single sign-on",
    sub: "Let people sign in to this tenant with GitHub or an OpenID Connect provider such as Google, Okta or Entra ID.",
    route: "/pam/settings/sso" },
];

// Design provider id ↔ API provider value.
//
// The AuthNull card must save as "authnull". It used to save as "expo", which
// SetMFAProvider accepts, so the save looked fine — but picking the app has to
// write a row that says `authnull`, because a tenant left on the deployment
// default keeps inheriting whatever MFA_PROVIDER is set to (Okta, here). Users
// then enrol phones, the QR scans, the panel counts them, and no push ever
// arrives: the challenge is going to an Okta org those users don't exist in.
const apiValue = (id) => id;
// "expo" and "fcm" are transport-level values for the same AuthNull app, so both
// display as the Authenticator card. An empty provider means nothing is set.
const displayId = (val) =>
  val === "expo" || val === "fcm" ? "authnull" : val || "authnull";

// Providers the backend may report that this screen has no card for (azure_ad,
// or anything added server-side later). Rendering falls back to a read-only
// summary rather than crashing on a missing card.
const PROVIDER_LABELS = { azure_ad: "Microsoft Entra ID", duo: "Duo Security", okta: "Okta Verify" };
const providerLabel = (id) =>
  PROVIDER_LABELS[id] || (id ? id.replace(/_/g, " ") : "MFA provider");

// Logos live in /public and are served at the app root.
const ASSET = (file) => `${process.env.PUBLIC_URL}/${file}`;

const PROVIDERS = [
  // No static "Default" tag: the deployment's MFA_PROVIDER decides what is
  // actually default, and on this deployment that is Okta. A card asserting
  // DEFAULT while Okta is the active provider contradicts the strip above it.
  // The tag is supplied at render time from `source`.
  { id: "authnull", name: "AuthNull Authenticator", tag: "No credentials required", mono: "∅", accent: C.primary,
    img: ASSET("logo_blue.png"), desc: "Native push approvals via the AuthNull mobile app." },
  { id: "duo", name: "Duo Security", tag: "Customer's own org", mono: "DUO", accent: "var(--an-success)",
    img: ASSET("duo.png"), desc: "Route approvals through your existing Duo deployment.",
    fields: [
      { key: "host", label: "API hostname", placeholder: "api-XXXXXXXX.duosecurity.com", mono: true },
      // Stored like the secret key: never echoed back, kept when left blank on save.
      { key: "ikey", label: "Integration key", placeholder: "DI...", mono: true, secret: true },
      { key: "skey", label: "Secret key", placeholder: "Enter secret key", secret: true },
    ] },
  { id: "okta", name: "Okta Verify", tag: "Customer's own org", mono: "OKTA", accent: "var(--an-ink3)",
    img: ASSET("okta-app.png"), desc: "Use Okta Verify push for AD authentication events.",
    fields: [
      { key: "domain", label: "Okta org URL", placeholder: "acme.okta.com", mono: true },
      { key: "apiToken", label: "API token", placeholder: "00T...", secret: true },
    ] },
];

// ── Atoms ────────────────────────────────────────────────────────────────
function ProviderGlyph({ p, size = 38, radius = 9 }) {
  if (p.img) {
    return (
      <span style={{
        width: size, height: size, borderRadius: radius, flexShrink: 0,
        background: "var(--an-surface)", border: `1px solid ${C.line}`,
        display: "inline-flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
      }}>
        <img src={p.img} alt={p.name || p.mono}
          style={{ maxWidth: size * 0.68, maxHeight: size * 0.68, objectFit: "contain" }} />
      </span>
    );
  }
  const big = p.mono.length <= 1;
  return (
    <span style={{
      width: size, height: size, borderRadius: radius, flexShrink: 0,
      background: `${anAlpha(p.accent, 7)}`, color: p.accent, border: `1px solid ${anAlpha(p.accent, 15)}`,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontFamily: big ? AN_FONT : AN_MONO, fontWeight: 700,
      fontSize: big ? size * 0.5 : Math.min(11, size * 0.28), letterSpacing: big ? 0 : 0.3,
    }}>{p.mono}</span>
  );
}

function EncNote() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: C.muted }}>
      <Icon name="lock" size={13} stroke={1.7} />
      Credentials are stored encrypted per organisation and never leave your tenant boundary.
    </div>
  );
}

// An informational line inside a settings card (icon + text), padded like a row.
function InfoLine({ children }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: SETTINGS_TOKENS.rowPad,
      fontSize: 13, color: C.ink2, lineHeight: 1.5 }}>
      <span style={{ color: C.primary, marginTop: 1, flexShrink: 0 }}><Icon name="info" size={16} /></span>
      <span>{children}</span>
    </div>
  );
}

// The bottom strip of a card: a note on the left, actions on the right.
function CardFooter({ children, actions }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
      padding: "14px 20px", background: C.bg, borderTop: `1px solid ${SETTINGS_TOKENS.hairline}` }}>
      <div style={{ minWidth: 0 }}>{children}</div>
      {actions && <div style={{ display: "flex", gap: 8 }}>{actions}</div>}
    </div>
  );
}

// Text input styled to match the design's inputs (hairline border + focus ring).
// The label, help and error come from the SettingsField around it.
function FieldInput({ value, onChange, placeholder, mono, secret, error }) {
  const [focus, setFocus] = useState(false);
  const borderColor = error ? C.danger : focus ? C.primary : C.line;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8, padding: "9px 11px", minHeight: 38, boxSizing: "border-box",
      background: "var(--an-surface)", borderRadius: 8,
      border: `1px solid ${borderColor}`,
      boxShadow: focus ? `0 0 0 3px ${error ? "var(--an-dangerBd)" : C.primaryL}` : "none",
      transition: "border-color .12s, box-shadow .12s",
      color: C.muted,
    }}>
      {secret && <Icon name="lock" size={13} stroke={1.6} />}
      <input
        type={secret ? "password" : "text"}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent",
          fontSize: 13, fontFamily: mono ? AN_MONO : AN_FONT, color: C.ink }}
      />
    </div>
  );
}

// Per-card footer tag. The saved provider's tag states *why* it is in effect,
// which is the distinction the old static tags erased: inherited from the
// deployment env is not the same as chosen by this organisation.
const cardTags = (savedProvider, source, configuredForOrg) => {
  const tags = {};
  PROVIDERS.forEach((p) => { tags[p.id] = p.tag; });
  if (source === "env-default") tags[savedProvider] = "Deployment default";
  else if (source === "org") {
    tags[savedProvider] = configuredForOrg
      ? "Active for this organisation"
      : "Incomplete configuration";
  }
  return tags;
};

// ── Provider selector (choice cards) ───────────────────────────────────────
// Only one external provider can be active at a time, so the inactive one is
// disabled (via `disabledIds`) until the active provider is removed. On an
// env-default org nothing is disabled: there is no saved row to remove first,
// and blocking the switch would leave a new tenant unable to pick the app.
function ProviderCards({ value, onChange, disabledIds = [], tags = {} }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
      {PROVIDERS.map((p) => {
        const on = p.id === value;
        const disabled = disabledIds.includes(p.id);
        return (
          <button key={p.id} type="button" onClick={() => !disabled && onChange(p.id)} disabled={disabled} style={{
            textAlign: "left", cursor: disabled ? "not-allowed" : "pointer", padding: 16,
            borderRadius: SETTINGS_TOKENS.cardRadius, fontFamily: AN_FONT,
            background: on ? C.primaryL : C.surface, border: `1px solid ${on ? C.primary : C.line}`,
            boxShadow: on ? `0 0 0 1px ${C.primary}` : "none",
            opacity: disabled ? 0.55 : 1, transition: "border-color .12s, background .12s",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <ProviderGlyph p={p} size={36} />
              <span style={{ width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                border: `1.5px solid ${on ? C.primary : C.line}`, background: on ? C.primary : "#fff", color: "#fff",
                display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                {on && <Icon name="check" size={11} stroke={2.4} />}
              </span>
            </div>
            <div style={{ fontSize: 14.5, fontWeight: 600, color: C.ink, marginTop: 12 }}>{p.name}</div>
            <div style={{ fontSize: 12.5, color: C.muted, marginTop: 3, lineHeight: 1.45 }}>{p.desc}</div>
            <div style={{ marginTop: 10, fontSize: 12, fontWeight: 500, color: on ? C.primary : C.muted }}>
              {disabled ? "Remove the active provider to switch" : (tags[p.id] || p.tag)}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// Contextual post-save / post-remove guidance banner.
function Banner({ tone = "info", children, action }) {
  const palette = tone === "success"
    ? { bg: "var(--an-successBg)", bd: "var(--an-successBd)", fg: C.success }
    : { bg: C.primaryL, bd: "var(--an-infoBd)", fg: C.primary };
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
      padding: "12px 16px", background: palette.bg, border: `1px solid ${palette.bd}`,
      borderRadius: SETTINGS_TOKENS.cardRadius, fontSize: 13, color: C.ink2, lineHeight: 1.5 }}>
      <span style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <span style={{ color: palette.fg, flexShrink: 0, marginTop: 2 }}><Icon name="info" size={15} /></span>
        {children}
      </span>
      {action}
    </div>
  );
}

// State 2 — a configured external provider, shown read-only with manage actions.
function ConnectedView({ provider, data, onReconfigure, onRemove, deleting }) {
  const conf = PROVIDERS.find((p) => p.id === provider);
  return (
    <>
      <SettingsRow icon={<ProviderGlyph p={conf} size={36} />} title={conf.name} description={conf.desc}
        value={<SettingsStatus tone="success">Active</SettingsStatus>} />
      {conf.fields.map((f, i) => (
        <SettingsRow key={f.key} title={f.label} last={i === conf.fields.length - 1}
          value={
            <span style={{ display: "block", fontFamily: f.mono ? AN_MONO : AN_FONT, color: C.ink,
              letterSpacing: f.secret ? 2 : 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {f.secret ? "••••••••••••" : (data[f.key] || "—")}
            </span>
          } />
      ))}
      <CardFooter actions={
        <>
          <Button onClick={onReconfigure}>Reconfigure</Button>
          <Button danger loading={deleting} onClick={onRemove}>
            {deleting ? "Removing…" : "Remove provider"}
          </Button>
        </>
      }>
        <EncNote />
      </CardFooter>
    </>
  );
}

// Credentials whose absence this screen can actually observe, used only to name
// what is missing on an incomplete row. GetMFAProvider echoes the non-secret
// `host`/`domain` and reports secrets as stored-or-not; it never echoes Duo's
// `ikey`, so that one is deliberately absent here — an unnameable gap falls back
// to the generic wording rather than accusing a field that may well be set.
// The backend stays the authority on completeness; this only phrases it.
const REQUIRED_FIELDS = {
  duo: [{ key: "host", label: "API hostname" }, { key: "skey", label: "Secret key" }],
  okta: [{ key: "domain", label: "Okta org URL" }, { key: "apiToken", label: "API token" }],
};

// ── MFA panel (live) ───────────────────────────────────────────────────────
function MFAPanel({
  provider, setProvider, savedProvider, savedData, fields, setField, savedSecrets,
  reconfiguring, onReconfigure, onRemove, deleting, errors, formError, banner, onGoToUsers,
  source, configuredForOrg,
}) {
  const active = PROVIDERS.find((p) => p.id === provider);
  const savedActive = PROVIDERS.find((p) => p.id === savedProvider);
  const savedName = savedActive?.name || providerLabel(savedProvider);
  const isEnvDefault = source === "env-default";
  // Only a provider with a saved row carrying its credentials is "configured".
  // This used to be `savedProvider === "duo" || "okta"`, which reported the
  // env-inherited Okta as connected and offered to remove a row that isn't there.
  const isConfigured = provider === savedProvider && configuredForOrg;
  const showConnected = isConfigured && !reconfiguring;
  // Only one external provider at a time — disable the other when one is
  // active. Nothing is disabled on env-default: there is no row to remove
  // first, and the switch this would block is exactly the one a new tenant
  // needs (picking the Authenticator).
  const disabledIds = isEnvDefault
    ? []
    : savedProvider === "duo" ? ["okta"] : savedProvider === "okta" ? ["duo"] : [];
  const tags = cardTags(savedProvider, source, configuredForOrg);

  // Which credential the incomplete row is missing. `savedData` holds only the
  // non-secret fields the API echoes back; a secret counts as present when the
  // API reported one stored.
  const missingFields = (REQUIRED_FIELDS[savedProvider] || [])
    .filter((f) => !savedData[f.key] && !savedSecrets[f.key])
    .map((f) => f.label);

  // Three distinct states, because "which provider" and "configured by whom"
  // are separate facts and only the pair is safe to act on. "Encrypted per
  // org" appears only when a per-org row actually holds credentials. On
  // env-default there is nothing encrypted per org: the provider is using the
  // deployment's own credentials.
  const status = isEnvDefault
    ? { tone: "neutral", label: "Deployment default", desc: "Inherited from the deployment — not configured for this organisation." }
    : configuredForOrg
      ? { tone: "success", label: "Active", desc: "Configured for this organisation · credentials encrypted per organisation." }
      : { tone: "warn", label: "Incomplete", desc: "Saved for this organisation, but its configuration is incomplete." };

  return (
    <SettingsPage
      title="MFA Provider"
      description="Choose how push MFA is delivered when users sign in with their directory account. Use the AuthNull Authenticator, or plug in your existing Duo or Okta deployment."
    >
      <SettingsSection title="Status" description="The provider push approvals go through right now.">
        <SettingsRow last icon={<Icon name="shield" />} title={savedName} description={status.desc}
          value={<SettingsStatus tone={status.tone}>{status.label}</SettingsStatus>} />
      </SettingsSection>

      {isEnvDefault && (
        <Banner action={provider === "authnull"
          // Already selected — the save bar is the next step, so no dead CTA here.
          ? null
          : <Button onClick={() => setProvider("authnull")}>Use the Authenticator</Button>}>
          <span>
            <strong style={{ fontWeight: 600 }}>{savedName} is inherited from the deployment</strong>, not configured
            for this organisation. Its users are looked up in the deployment's {savedName} account — users who exist
            only in this organisation will not be found there, and every challenge for them reports not-enrolled.
            Choose a provider below and save to configure this organisation explicitly.
          </span>
        </Banner>
      )}
      {!isEnvDefault && !configuredForOrg && savedActive?.fields && (
        <Banner>
          <span>
            <strong style={{ fontWeight: 600 }}>{savedName} has an incomplete configuration.</strong>{" "}
            {missingFields.length
              ? `Missing: ${missingFields.join(", ")}. Push approvals will fail until it is supplied.`
              : "A required credential is missing. Push approvals will fail until it is supplied."}
          </span>
        </Banner>
      )}

      {banner === "duo" && <Banner tone="success">Duo configured. Ensure your AD users are enrolled in Duo Mobile before MFA enforcement begins.</Banner>}
      {banner === "okta" && <Banner tone="success">Okta configured. Ensure your AD users have Okta Verify enrolled in your Okta org.</Banner>}
      {banner === "authnull" && (
        <Banner action={<Button onClick={onGoToUsers}>Go to Users</Button>}>
          AuthNull Authenticator is now active. Send enrollment emails to your users.
        </Banner>
      )}

      <SettingsSection bare title="Provider" description="Select the source for push approvals.">
        <ProviderCards value={provider} onChange={setProvider} disabledIds={disabledIds} tags={tags} />
      </SettingsSection>

      {/* A provider reported by the backend that this screen has no card for
          (azure_ad, or one added server-side later). Read-only rather than a
          crash on the missing card. */}
      {!active ? (
        <SettingsSection title="Connection details" description={providerLabel(provider)}>
          <InfoLine>
            This organisation uses <strong style={{ fontWeight: 600 }}>{providerLabel(provider)}</strong>, which
            is configured outside this screen. Second factors are enrolled with the provider, not with AuthNull.
          </InfoLine>
        </SettingsSection>
      ) : (
        <SettingsSection title="Connection details" description={active.name}>
          {!active.fields ? (
            <InfoLine>
              No credentials to configure — the app talks to AuthNull directly. Users receive push approvals via
              the AuthNull Authenticator. Enroll them per user from
              <strong style={{ fontWeight: 600 }}> Users</strong>.
            </InfoLine>
          ) : showConnected ? (
            <ConnectedView provider={provider} data={savedData} deleting={deleting}
              onReconfigure={onReconfigure} onRemove={onRemove} />
          ) : (
            <>
              {active.fields.map((f, i) => {
                const wasSaved = f.secret && savedSecrets[f.key] && provider === savedProvider;
                return (
                  <SettingsField key={f.key} label={f.label} required error={errors[f.key]}
                    last={i === active.fields.length - 1 && !formError}
                    help={wasSaved ? "A value is already stored. Leave blank to keep it unchanged." : undefined}>
                    <FieldInput mono={f.mono} secret={f.secret} error={errors[f.key]}
                      value={fields[f.key]} onChange={(v) => setField(f.key, v)}
                      placeholder={wasSaved ? "Leave blank to keep the current key" : f.placeholder} />
                  </SettingsField>
                );
              })}
              {formError && (
                <div style={{ padding: SETTINGS_TOKENS.rowPad }}>
                  <div style={{ padding: "10px 12px", background: "var(--an-dangerBg)", border: "1px solid var(--an-dangerBd)",
                    borderRadius: 8, fontSize: 12.5, color: C.danger }}>
                    {formError}
                  </div>
                </div>
              )}
              <CardFooter><EncNote /></CardFooter>
            </>
          )}
        </SettingsSection>
      )}
    </SettingsPage>
  );
}

// ── Settings home — the overview ───────────────────────────────────────────
// `meta` overrides the right-hand text for sections that aren't configured but
// navigated to (e.g. Users & Access, which is a list, not a config form).
// Row values for the sections whose state is read live on this page.
// getLogOutputConfiguration's outputName is the tenant's active output ("" when
// none); the API spells two of them differently from the page titles.
const LOG_OUTPUT_LABEL = { Splunk: "Splunk", Http: "HTTP", Syslog: "Syslog", ElasticSearch: "Elasticsearch" };
const LIVE_SECTIONS = ["logs", "recordings", "sso"];
const STORAGE_LABEL = { s3: "Amazon S3", gcs: "Google Cloud Storage" };

function useSectionStates() {
  const [states, setStates] = useState({});
  useEffect(() => {
    let alive = true;
    const put = (id, v) => alive && setStates((s) => ({ ...s, [id]: v }));

    axios.post(`${REACT_APP_API_TENANTS_URL}/getLogOutputConfiguration`,
      { orgId: ORG_ID(), tenantId: DOMAIN_ID() },
      { headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() } })
      .then((res) => {
        const name = res?.data?.outputName;
        put("logs", name ? { status: `Active · ${LOG_OUTPUT_LABEL[name] || name}` } : { meta: "Not configured" });
      })
      .catch(() => put("logs", { meta: "Status unavailable", action: "Open" }));

    if (isAdminUser()) {
      getRecordingStorage()
        .then((data) => {
          const cfg = data?.config || {};
          const label = STORAGE_LABEL[cfg.provider];
          if (!label) { put("recordings", { status: "Local volume" }); return; }
          const where = cfg.bucket ? `${label} · ${cfg.bucket}` : label;
          put("recordings", cfg.lastTestOk === false
            ? { meta: `${where} — last test failed`, action: "Review" }
            : { status: where });
        })
        .catch(() => put("recordings", { meta: "Status unavailable", action: "Open" }));
      // A provider is offered on the sign-in page only while enabled with a stored secret.
      listSsoProviders(DOMAIN_ID())
        .then((data) => {
          const ps = Array.isArray(data?.providers) ? data.providers : [];
          const live = ps.filter((p) => p.enabled && p.hasSecret);
          if (!ps.length) { put("sso", { meta: "Not configured" }); return; }
          put("sso", live.length
            ? { status: live.length === 1 ? `Active · ${live[0].name}` : `${live.length} providers active` }
            : { meta: `${ps.length} configured, none active`, action: "Review" });
        })
        .catch(() => put("sso", { meta: "Status unavailable", action: "Open" }));
    } else {
      put("recordings", { meta: "Administrators only", action: "View" });
      put("sso", { meta: "Administrators only", action: "View" });
    }
    return () => { alive = false; };
  }, []);
  return states;
}

// Settings overview, laid out like a browser's settings home: the signed-in
// account on top, then each setting as a row (icon, what it is, its state, ›).
function AccountCard() {
  const email = localStorage.getItem("UserName") || "";
  const name = email.split("@")[0] || "User";
  const role = String(localStorage.getItem("UserRole") || "").toLowerCase().includes("admin") ? "Administrator" : "User";
  const initials = (name.match(/[A-Za-z0-9]+/g) || []).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "U";
  const tenant = (localStorage.getItem("URL") || "").replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  return (
    <div style={{ padding: 20, display: "flex", alignItems: "center", gap: 18 }}>
      <span style={{ width: 64, height: 64, borderRadius: "50%", background: C.warn, color: "#fff", flexShrink: 0,
        display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 600 }}>
        {initials}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: C.ink }}>{name}</div>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>{email}</div>
        <div style={{ marginTop: 6 }}>
          <SettingsStatus tone="success">
            {role}{tenant ? <span style={{ color: C.muted, fontWeight: 400 }}> · {tenant}</span> : null}
          </SettingsStatus>
        </div>
      </div>
    </div>
  );
}

// Right-hand value of an overview row: a live status in a SettingsStatus, a
// plain muted note otherwise ("Tenant accounts", "Loading…").
function rowValue({ status, tone = "success", meta, live }) {
  if (status) return <SettingsStatus tone={tone}>{status}</SettingsStatus>;
  if (live) return <SettingsStatus tone={tone === "warn" ? "warn" : "neutral"}>{meta ?? "Not configured"}</SettingsStatus>;
  return <span style={{ fontSize: 12.5, color: C.muted }}>{meta ?? "Not configured"}</span>;
}

function SettingsHome({ mfaStatus, mfaTone, onNavigate }) {
  const live = useSectionStates();
  return (
    <div style={{ display: "grid", gap: 24 }}>
      <SettingsSection title="Your account">
        <AccountCard />
      </SettingsSection>
      <SettingsSection title="Tenant settings"
        description="These apply to everyone on this tenant, in every product (AD, Database, RADIUS and PAM).">
        {SECTION_META.map((s, i) => {
          const state = live[s.id];
          const loaded = LIVE_SECTIONS.includes(s.id) && !!state;
          const value = s.id === "mfa"
            ? rowValue({ status: mfaStatus, tone: mfaTone })
            : rowValue({
                status: state?.status,
                tone: state?.action === "Review" ? "warn" : "success",
                meta: state?.meta ?? (LIVE_SECTIONS.includes(s.id) && !state ? "Loading…" : s.meta),
                live: loaded,
              });
          return (
            <SettingsRow key={s.id} icon={<Icon name={s.icon} size={18} />} title={s.title} description={s.sub}
              value={value} last={i === SECTION_META.length - 1} onClick={() => onNavigate(s.route)} />
          );
        })}
      </SettingsSection>
    </div>
  );
}

// `envDefault` distinguishes "nothing to save" from "nothing saved for this
// org" — on an env-default org the clean state is not a settled state, and
// "All changes saved" would read as though a choice had been recorded.
// Sticky at the bottom of the settings scroll area, styled as a settings card.
function SaveBar({ dirty, saving, onSave, onDiscard, canSave, envDefault }) {
  const tone = dirty || envDefault ? "warn" : "success";
  const label = dirty
    ? "Unsaved changes"
    : envDefault ? "No provider saved for this organisation" : "All changes saved";
  return (
    <div style={{ position: "sticky", bottom: 16, zIndex: 2 }}>
      <SettingsCard style={{ boxShadow: "0 6px 20px rgba(16,22,60,.08)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
          flexWrap: "wrap", padding: "12px 20px" }}>
          <SettingsStatus tone={tone}>{label}</SettingsStatus>
          <div style={{ display: "flex", gap: 8 }}>
            <Button disabled={!dirty} onClick={onDiscard}>Discard</Button>
            <Button type="primary" loading={saving} disabled={!canSave} onClick={onSave}>
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </SettingsCard>
    </div>
  );
}

// Load the Inter / JetBrains Mono webfonts once (the design relies on them).
function useDesignFonts() {
  useEffect(() => {
    const id = "an-design-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap";
    document.head.appendChild(link);
  }, []);
}

// ── Screen ───────────────────────────────────────────────────────────────
export default function SettingsScreen({ section = "home" }) {
  useDesignFonts();
  const navigate = useNavigate();
  // The overview, or the MFA provider page (/pam/settings/mfaProvider).
  const view = section === "mfa" ? "mfa" : null;
  const [, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [provider, setProvider] = useState("authnull");
  const [savedProvider, setSavedProvider] = useState("authnull");
  const [savedData, setSavedData] = useState({});
  const [fields, setFields] = useState({});
  const [savedSecrets, setSavedSecrets] = useState({});
  const [dirty, setDirty] = useState(false);
  const [reconfiguring, setReconfiguring] = useState(false);
  const [banner, setBanner] = useState(null);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  // "org" = an explicit choice saved for this organisation · "env-default" = no
  // row exists and MFA_PROVIDER from the deployment was inherited. Assume
  // env-default until told otherwise: it is the state that suppresses claims.
  const [source, setSource] = useState("env-default");
  const [configuredForOrg, setConfiguredForOrg] = useState(false);

  const conf = PROVIDERS.find((p) => p.id === provider);

  const loadProvider = useCallback(() => {
    setLoading(true);
    dispatchGetMFAProvider({
      payload: { orgId: ORG_ID() },
      onSuccess: (data) => {
        setLoading(false);
        const id = displayId(data.provider);
        const configured = !!data.configuredForOrg;
        // Trust configuredForOrg over the echoed host/domain for whether a
        // secret is stored: on env-default those come back empty while the
        // provider still works off the deployment's own credentials.
        const prefill = {};
        const secrets = {};
        if (id === "duo") { prefill.host = data.host || ""; secrets.ikey = configured; secrets.skey = configured; }
        if (id === "okta") { prefill.domain = data.domain || ""; secrets.apiToken = configured; }
        setSource(data.source === "org" ? "org" : "env-default");
        setConfiguredForOrg(configured);
        setProvider(id);
        setSavedProvider(id);
        setSavedData(prefill);
        setFields(prefill);
        setSavedSecrets(secrets);
        setReconfiguring(false);
        setDirty(false);
        setBanner(null);
        setErrors({});
        setFormError("");
      },
      onFailure: ({ message: msg, isLogout }) => {
        setLoading(false);
        if (isLogout) { message.error(msg || "Credentials are invalid"); logoutHandler(); return; }
        message.error("Could not load MFA configuration.");
      },
      onError: () => { setLoading(false); message.error("Could not load MFA configuration."); },
    });
  }, []);

  useEffect(() => { loadProvider(); }, [loadProvider]);

  // Field keys never overlap across providers (duo: host/ikey/skey · okta: domain/apiToken),
  // so we can keep accumulated values without clearing on switch.
  const selectProvider = (id) => {
    if (id === provider) return;
    setProvider(id);
    setReconfiguring(false);
    setBanner(null);
    setErrors({});
    setFormError("");
    setDirty(id !== savedProvider);
  };

  const setField = (key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    if (savedSecrets[key]) setSavedSecrets((prev) => ({ ...prev, [key]: false }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    setFormError("");
    setBanner(null);
    setDirty(true);
  };

  const validate = () => {
    const errs = {};
    (conf?.fields || []).forEach((f) => {
      const val = (fields[f.key] || "").trim();
      const wasSaved = f.secret && savedSecrets[f.key] && provider === savedProvider;
      if (!val && !wasSaved) { errs[f.key] = `${f.label} is required`; return; }
      if (val && f.key === "host" && !/^api-[\w-]+\.duosecurity\.com$/i.test(val))
        errs[f.key] = "Must look like api-XXXXXXXX.duosecurity.com";
      if (val && f.key === "domain" && !/^https:\/\//i.test(val))
        errs[f.key] = "Must start with https://";
    });
    return errs;
  };

  const handleSave = () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setFormError("");

    const payload = { orgId: ORG_ID(), tenantId: DOMAIN_ID(), provider: apiValue(provider) };
    (conf?.fields || []).forEach((f) => {
      const val = fields[f.key];
      const wasSaved = f.secret && savedSecrets[f.key] && provider === savedProvider;
      if (!f.secret && val) payload[f.key] = val;
      if (f.secret && val && !wasSaved) payload[f.key] = val;
    });

    setSaving(true);
    dispatchSetMFAProvider({
      payload,
      onSuccess: () => {
        setSaving(false);
        setSavedProvider(provider);
        // A row now exists for this org, carrying whatever the save just
        // validated — so this org is no longer inheriting the deployment default.
        setSource("org");
        setConfiguredForOrg(true);
        const nextData = {};
        (conf?.fields || []).filter((f) => !f.secret).forEach((f) => { nextData[f.key] = fields[f.key] || ""; });
        setSavedData(nextData);
        const newSecrets = {};
        (conf?.fields || []).filter((f) => f.secret).forEach((f) => {
          newSecrets[f.key] = !!fields[f.key] || savedSecrets[f.key];
        });
        setSavedSecrets(newSecrets);
        setReconfiguring(false);
        setDirty(false);
        setErrors({});
        setFormError("");
        setBanner(provider);
        message.success("MFA provider saved");
      },
      onFailure: ({ message: msg, isLogout }) => {
        setSaving(false);
        if (isLogout) { message.error(msg || "Credentials are invalid"); logoutHandler(); return; }
        setFormError(msg || "Failed to save. Please try again.");
      },
      onError: ({ message: msg }) => { setSaving(false); message.error(msg || "Failed to save. Please try again."); },
    });
  };

  const handleReconfigure = () => {
    setReconfiguring(true);
    setBanner(null);
    setErrors({});
    setFormError("");
    setFields({ ...savedData });
  };

  const handleRemove = () => {
    Modal.confirm({
      title: "Remove MFA provider?",
      // Removing the row does not necessarily land on the Authenticator: with
      // no row, the org inherits MFA_PROVIDER from the deployment, which on this
      // deployment is Okta. loadProvider() below re-reads the resulting state
      // rather than assuming.
      content: "This deletes this organisation's provider configuration. MFA will fall back to the deployment default until you choose a provider. Continue?",
      okText: "Remove provider",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () => new Promise((resolve) => {
        setDeleting(true);
        dispatchDeleteMFAProvider({
          payload: { orgId: ORG_ID() },
          onSuccess: () => {
            setDeleting(false);
            setSavedData({});
            setFields({});
            setSavedSecrets({});
            setReconfiguring(false);
            setErrors({});
            setFormError("");
            setDirty(false);
            setBanner(null);
            message.success("Provider configuration removed");
            // Re-read rather than assume "authnull": with the row gone the org
            // falls back to the deployment's MFA_PROVIDER, whatever that is.
            loadProvider();
            resolve();
          },
          onFailure: ({ message: msg, isLogout }) => {
            setDeleting(false);
            if (isLogout) { message.error(msg || "Credentials are invalid"); logoutHandler(); }
            else message.error(msg || "Failed to remove provider.");
            resolve();
          },
          onError: () => { setDeleting(false); message.error("Failed to remove provider."); resolve(); },
        });
      }),
    });
  };

  const isComplete = !conf?.fields || conf.fields.every((f) => {
    const val = (fields[f.key] || "").trim();
    const wasSaved = f.secret && savedSecrets[f.key] && provider === savedProvider;
    return !!val || wasSaved;
  });
  const isConfigured = provider === savedProvider && configuredForOrg;
  const showConnected = isConfigured && !reconfiguring;
  // Selecting the Authenticator on an env-default org must be savable: that is
  // the path a new tenant takes to stop inheriting the deployment's provider.
  // `dirty` alone is not enough — when the inherited provider already *is* the
  // Authenticator, picking it changes no field, so nothing is dirty and the
  // save would stay disabled with no row ever written. On env-default the save
  // is always offered, because writing the row is itself the change.
  const canSave = (dirty || source === "env-default") && isComplete && !showConnected;
  const savedName = PROVIDERS.find((p) => p.id === savedProvider)?.name || providerLabel(savedProvider);
  const mfaStatus = source === "env-default"
    ? `Deployment default · ${savedName}`
    : configuredForOrg ? `Active · ${savedName}` : `Incomplete · ${savedName}`;
  const mfaTone = source === "env-default" ? "neutral" : configuredForOrg ? "success" : "warn";

  // Shown inside SettingsShell, which provides the menu, background, width and scroll.
  if (view !== "mfa") return <SettingsHome mfaStatus={mfaStatus} mfaTone={mfaTone} onNavigate={navigate} />;
  return (
    <div style={{ display: "grid", gap: 24 }}>
      <MFAPanel provider={provider} setProvider={selectProvider} savedProvider={savedProvider}
        savedData={savedData} fields={fields} setField={setField} savedSecrets={savedSecrets}
        reconfiguring={reconfiguring} onReconfigure={handleReconfigure} onRemove={handleRemove}
        deleting={deleting} errors={errors} formError={formError} banner={banner}
        source={source} configuredForOrg={configuredForOrg}
        onGoToUsers={() => navigate("/pam/users")} />
      <SaveBar dirty={dirty} saving={saving} canSave={canSave} onSave={handleSave}
        onDiscard={loadProvider} envDefault={source === "env-default"} />
      {/* The Authenticator's own settings, once it is the provider in force:
          they would act on nothing while Duo or Okta handles push. */}
      {provider === "authnull" && savedProvider === "authnull" && <AuthenticatorSettings />}
      {/* Pushes through any provider (Authenticator, Okta, Duo) are recorded,
          so the history shows whichever one is in force. */}
      {savedProvider && isAdminUser() && <PushHistorySection provider={savedProvider} />}
    </div>
  );
}
